import { useState, forwardRef } from 'react';
import { GraduationCap, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeedbackCard from '../common/FeedbackCard';

const GradeCard = forwardRef(function GradeCard({ examScores, reportType }, ref) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!examScores) return null;

  const label = reportType === 'midterm'
    ? t('report.midtermScores', 'Midterm Scores')
    : t('report.finalScores', 'Final Scores');

  const badgeColor = reportType === 'midterm'
    ? 'bg-emerald-500 dark:bg-transparent dark:border dark:border-emerald-500'
    : 'bg-teal-500 dark:bg-transparent dark:border dark:border-teal-500';

  return (
    <div
      ref={ref}
      className="bg-white dark:bg-gray-800 rounded-xl mb-3 sm:mb-4 shadow-sm hover:shadow-md dark:shadow-gray-900/30 transition-shadow duration-200 overflow-hidden animate-fade-in border border-gray-200 dark:border-gray-700"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-3 sm:p-5"
      >
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${badgeColor} text-white dark:text-emerald-400`}>
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                {label}
              </h3>
              {examScores.total && (
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {examScores.total}
                </p>
              )}
            </div>
          </div>
          <div className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
            isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}>
            <ChevronDown
              className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
      </button>

      {/* Expanded Content */}
      <div className="accordion-wrapper" data-open={isOpen}>
        <div className="accordion-content">
          <div className="bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 px-3 py-3 sm:px-5 sm:py-4">

            {/* Score chips */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
              {examScores.mcq && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                  MCQ: {examScores.mcq}
                </span>
              )}
              {examScores.frq && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                  FRQ: {examScores.frq}
                </span>
              )}
              {examScores.total && examScores.mcq && examScores.frq && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800">
                  Total: {examScores.total}
                </span>
              )}
              {/* The server says what the scaled score is: "AMC Score: 102/150", "ACT Composite: 30",
                  or the AP default for older reports that carry no label. */}
              {examScores.ap_score && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                  {examScores.scaled_label || 'AP Score'}: {examScores.ap_score}{examScores.scaled_max ? `/${examScores.scaled_max}` : ''}
                </span>
              )}
            </div>

            {/* AI Feedback — shared component with class feedback */}
            <FeedbackCard
              label={reportType === 'midterm'
                ? t('report.midtermFeedback', 'Midterm Feedback')
                : t('report.finalFeedback', 'Final Feedback')}
              textKr={examScores.ai_feedback}
              textEn={examScores.ai_feedback_en}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default GradeCard;
